// AddCardholder.cshtml: a new cardholder account in a program the admin may assign to. In a program whose cards staff issue (instant issue) the
// same page issues the card instead: the person, the amount ($25, $50, $100, any other, or none) and the address the
// processor needs — the card is opened (and loaded) at once, nobody is invited (adminApi.issueInstantCard).
// Laid out in sections (card, cardholder, mailing address) with the action and what it will do at the end.
import { newIdempotencyKey } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { adminApi, type CardIssued } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { useStaff } from '../components/RequireStaff';
import { FormError, PageHeading } from '../components/ui';
import { assignablePrograms } from '../scope';
import { US_STATES } from '../usStates';

/** The amounts offered with one click; any other can be typed. */
export const ISSUE_AMOUNTS = [25, 50, 100] as const;

/** "50", "50.5", "$1,250.00" → 1250; null when it is not a positive amount with at most two decimals. */
export function issueAmount(raw: string): number | null {
  const text = raw.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const value = Number(text);
  return value > 0 ? value : null;
}

const dollars = (value: number) => `$${value.toFixed(2)}`;

function Required() {
  return (
    <span className="qc-required" aria-hidden="true">
      *
    </span>
  );
}

export function AddCardholderPage() {
  usePageTitle('Add cardholder');
  const { scope } = useStaff();
  const navigate = useNavigate();
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs, enabled: !scope.needsProvider });
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [preset, setPreset] = useState<number | 'other' | 'none'>(50);
  const [other, setOther] = useState('');
  const [issued, setIssued] = useState<(CardIssued & { name: string; amount: number | null }) | null>(null);
  if (scope.needsProvider) return <Navigate to="/cardholders" replace />;
  const options = assignablePrograms(programs.data ?? [], scope);
  const programId = chosen ?? options[0]?.id ?? '';
  const program = options.find((p) => p.id === programId) ?? null;
  const instant = program?.instantIssue === true;
  const noLoad = preset === 'none';
  const amount = noLoad ? null : preset === 'other' ? issueAmount(other) : preset;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setExisting(null);
    const form = new FormData(e.currentTarget);
    const field = (name: string) => String(form.get(name) ?? '').trim();
    if (!options.some((p) => p.id === programId)) {
      setError(
        scope.internal
          ? `You can only add cardholders to a ${scope.activeProvider} client.`
          : 'You can only add cardholders to your own client.',
      );
      return;
    }
    if (instant) {
      if (!noLoad && amount === null) return setError('Enter the amount to load, e.g. 75 or 25.50.');
      setBusy(true);
      const result = await adminApi.issueInstantCard(
        {
          programId,
          email: field('NewEmail'),
          firstName: field('NewFirstName'),
          lastName: field('NewLastName'),
          phone: field('NewPhone') || null,
          amount,
          address: {
            street: field('Street'),
            city: field('City'),
            region: field('Region'),
            postalCode: field('PostalCode'),
            country: 'USA',
          },
        },
        newIdempotencyKey(),
      );
      setBusy(false);
      if (!result.success) {
        setExisting(result.cardholderId);
        return setError(result.message ?? "Couldn't issue this card.");
      }
      setError(null);
      setIssued({ ...result, amount, name: `${field('NewFirstName')} ${field('NewLastName')}`.trim() });
      return;
    }
    setBusy(true);
    const lastName = field('NewLastName');
    const result = await adminApi.createCardholder({
      email: field('NewEmail'),
      firstName: field('NewFirstName'),
      lastName: lastName === '' ? null : lastName,
      programId,
    });
    setBusy(false);
    if (!result.success) return setError(result.error ?? "Couldn't create this cardholder.");
    navigate('/cardholders');
  }

  if (issued)
    return (
      <>
        <PageHeading title="Issue card" trail={[['Cardholders', '/cardholders']]} />
        <div className="qc-panel qc-form-panel">
          <div className="qc-panel-title">
            <h3>Card issued</h3>
            <span>{program?.displayName}</span>
          </div>
          <p className="qc-issued-summary">
            <span>{issued.name}</span>
            <span className="qc-issued-card">•••• {issued.last4 ?? '----'}</span>
            <strong>{issued.amount === null ? 'No load' : dollars(issued.amount)}</strong>
          </p>
          {issued.message && (
            <p className="qc-form-warning" role="status">
              {issued.message}
            </p>
          )}
          <div className="qc-form-actions">
            <span className="qc-field-hint">No e-mail was sent: the cardholder is not invited.</span>
            <div className="qc-form-buttons">
              <button type="button" className="qc-button-ghost" onClick={() => setIssued(null)}>
                Issue another card
              </button>
              <Link to={`/cardholders/${issued.cardholderId}`} className="qor-button qor-button-primary">
                Open cardholder
              </Link>
            </div>
          </div>
        </div>
      </>
    );

  return (
    <>
      <PageHeading title={instant ? 'Issue card' : 'Add cardholder'} trail={[['Cardholders', '/cardholders']]} />

      <div className="qc-panel qc-form-panel">
        <div className="qc-panel-title">
          <h3>{instant ? 'New card' : 'New cardholder'}</h3>
        </div>
        <form onSubmit={(e) => void submit(e)} className="qc-form-grid">
          {instant && (
            <div className="qc-form-section">
              <h4>Card</h4>
            </div>
          )}
          <label className="qc-account-field qc-span-2">
            <span>Program</span>
            <select name="NewProgramId" value={programId} onChange={(e) => setChosen(e.target.value)}>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName} &mdash; {o.provider}
                </option>
              ))}
            </select>
            <small className="qc-field-hint">
              {instant
                ? "Staff issue this program's cards: the card is opened now, loaded with its amount if one is chosen. No invitation, no identity check."
                : 'The cardholder gets an e-mail to set a password, then applies for their card.'}
            </small>
          </label>

          {instant && (
            <fieldset className="qc-account-field qc-span-2">
              <legend>
                Amount (USD) <Required />
              </legend>
              <div className="qc-amount-row">
                <div className="qc-segmented" role="group" aria-label="Amount">
                  {ISSUE_AMOUNTS.map((value) => (
                    <button key={value} type="button" aria-pressed={preset === value} onClick={() => setPreset(value)}>
                      ${value}
                    </button>
                  ))}
                  <button type="button" aria-pressed={preset === 'other'} onClick={() => setPreset('other')}>
                    Other
                  </button>
                  <button type="button" aria-pressed={noLoad} onClick={() => setPreset('none')}>
                    No load
                  </button>
                </div>
                {preset === 'other' && (
                  <span className="qc-money-input">
                    <span aria-hidden="true">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="Amount"
                      aria-label="Other amount"
                      placeholder="0.00"
                      autoFocus
                      required
                      value={other}
                      onChange={(e) => setOther(e.target.value)}
                    />
                  </span>
                )}
              </div>
              <small className="qc-field-hint">
                Loaded once when the card is issued; staff can add funds later. &ldquo;No load&rdquo; issues the card
                with a $0.00 balance.
              </small>
            </fieldset>
          )}

          {instant && (
            <div className="qc-form-section">
              <h4>Cardholder</h4>
            </div>
          )}
          <label className="qc-account-field">
            <span>
              First name <Required />
            </span>
            <input type="text" name="NewFirstName" autoComplete="off" required maxLength={instant ? 50 : 120} />
          </label>
          <label className="qc-account-field">
            <span>Last name {instant && <Required />}</span>
            <input
              type="text"
              name="NewLastName"
              autoComplete="off"
              required={instant}
              maxLength={instant ? 50 : 120}
            />
          </label>
          <label className="qc-account-field">
            <span>
              Email <Required />
            </span>
            <input type="email" name="NewEmail" placeholder="client@example.com" autoComplete="off" required />
          </label>
          {instant && (
            <label className="qc-account-field">
              <span>
                Phone <span className="qc-field-hint">(optional)</span>
              </span>
              <input type="tel" name="NewPhone" placeholder="+1 202 555 0143" autoComplete="off" />
            </label>
          )}

          {instant && (
            <>
              <div className="qc-form-section">
                <h4>Mailing address</h4>
                <p>The processor opens the card on it.</p>
              </div>
              <label className="qc-account-field qc-span-2">
                <span>
                  Street address <Required />
                </span>
                <input type="text" name="Street" placeholder="141 Washington Ave" required maxLength={200} />
              </label>
              <label className="qc-account-field">
                <span>
                  City <Required />
                </span>
                <input type="text" name="City" autoComplete="off" required maxLength={50} />
              </label>
              <label className="qc-account-field">
                <span>
                  State <Required />
                </span>
                <select name="Region" required defaultValue="">
                  <option value="" disabled>
                    Choose a state…
                  </option>
                  {US_STATES.map(([code, stateName]) => (
                    <option key={code} value={stateName}>
                      {stateName} ({code})
                    </option>
                  ))}
                </select>
              </label>
              <label className="qc-account-field">
                <span>
                  ZIP code <Required />
                </span>
                <input
                  type="text"
                  name="PostalCode"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="12205"
                  pattern="\d{5}(-\d{4})?"
                  title="5 digits, or ZIP+4 like 12205-1234"
                  required
                  maxLength={10}
                />
              </label>
              <div className="qc-account-field">
                <span>Country</span>
                <p className="qc-static-value">
                  United States <span className="qc-field-hint">· these are US cards</span>
                </p>
              </div>
            </>
          )}

          {(error || existing) && (
            <div className="qc-span-2">
              <FormError message={error} />
              {existing && (
                <p style={{ margin: '6px 0 0' }}>
                  <Link to={`/cardholders/${existing}`}>
                    Open their card to add funds <span aria-hidden="true">&rsaquo;</span>
                  </Link>
                </p>
              )}
            </div>
          )}

          <div className="qc-form-actions qc-span-2">
            <span className="qc-field-hint">
              {instant
                ? noLoad
                  ? 'Issues the card with a $0.00 balance. Only staff add money afterwards.'
                  : amount === null
                    ? 'Enter the amount to load.'
                    : `Loads ${dollars(amount)} now. Only staff add money afterwards.`
                : 'Creates the account and e-mails a code to set a password.'}
            </span>
            <div className="qc-form-buttons">
              <Link to="/cardholders" className="qc-button-ghost">
                Cancel
              </Link>
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                {instant
                  ? busy
                    ? 'Issuing…'
                    : `Issue card${amount === null ? '' : ` · ${dollars(amount)}`}`
                  : busy
                    ? 'Adding…'
                    : 'Add cardholder'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
